<!-- MyComponent.vue -->
<script setup lang="ts">
import { ArrowUpDown } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import TheMap from '@/components/mode-map/TheMap.vue';
import useResponsive from '@/composables/useResponsive';
import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useMetricsStore } from '@/stores/useMetricsStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheSortingFilter from '../mode-list-filters/TheSortingFilter.vue';
import DistributorFilterDropdown from './DistributorFilterDropdown.vue';
import TheDurationDropdown from './TheDurationDropdown.vue';
import TheGrid from './TheGrid.vue';
import TheMobileSlider from './TheMobileSlider.vue';
import TheMultiselectDropdown from './TheMultiselectDropdown.vue';
import ThePriceDropdown from './ThePriceDropdown.vue';
import TheSelectDropdown from './TheSelectDropdown.vue';
import TheTimeRangeDropdown from './TheTimeRangeDropdown.vue';

const { t } = useI18n();
const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();
const { totalResults } = storeToRefs(productsStore);

const metricsStore = useMetricsStore();
const { places, taxonomyFilter } = storeToRefs(metricsStore);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const { isMobile } = useResponsive();

const sortingOptions2 = [
  { label: t('menu.relevance'), value: 'random' },
  { label: t('menu.price_asc'), value: 'price_from_ascending' },
  { label: t('menu.price_desc'), value: 'price_from_descending' },
  { label: t('menu.ranking'), value: 'rating' },
];

const timeRangeOptions = [
  { label: t('timeRange.morning'), minTime: 480, maxTime: 720, count: 0 },
  { label: t('timeRange.afternoon'), minTime: 720, maxTime: 1020, count: 0 },
  { label: t('timeRange.evening'), minTime: 1020, maxTime: 1440, count: 0 },
];

const isSortingOpen = ref<boolean>(false);
const sortingMode = ref(sortingOptions2[0]);
const isMapDisplay = ref(false);
const hasCalendar = computed(() => globalConfiguration.value?.options?.hasCalendar ?? true);
const shouldDisplayPriceDropdown = computed(() => globalConfiguration.value?.options?.hasFilters?.price);

const distributorId = computed(() => taxonomyFilter.value?.filter.filter.distributor_id ?? '');
const taxonomyFilterName = computed(() => {
  const name = taxonomyFilter.value?.name.find(it => it.lang === 'fr-FR')?.value ?? null;
  if (!name)
    return 'Filtre Distributeur';
  return name[0].toUpperCase() + name.substring(1);
});

function handleSortingFilter(option: any) {
  sortingMode.value = option;
  isSortingOpen.value = false;
  filtersStore.addSortingFilter(option.value);
}

function toggleMap(): boolean {
  return isMapDisplay.value = !isMapDisplay.value;
}

onMounted(async () => {
  filtersStore.loadFromStorage();
  await productsStore.fetchProductsList(filters.value);
  await metricsStore.getBaseMetrics(filters.value);
  await metricsStore.getDistributorFilter();
});
</script>

<template>
  <div class="yb-preset">
    <div v-if="!isMobile" class="dropdowns_wrapper">
      <div class="filters">
        <ThePriceDropdown v-if="shouldDisplayPriceDropdown" :placeholder="t('menu.price')" />
        <TheDurationDropdown v-if="hasCalendar" :placeholder="t('menu.duration')" />
        <TheTimeRangeDropdown v-if="hasCalendar" :placeholder="t('menu.moment')" :options="timeRangeOptions" />
        <TheMultiselectDropdown
          v-if="places.length" :placeholder="t('menu.place')" :options="places"
          @update:model-value="filtersStore.addPlacesFilters"
        />
        <DistributorFilterDropdown
          v-if="taxonomyFilter?.taxonomies.length" :placeholder="taxonomyFilterName"
          :options="taxonomyFilter.taxonomies"
          @update:model-value="(data) => filtersStore.handleDistributorFilters(data, distributorId)"
        />
      </div>
      <div class="sorting">
        <p class="sorting_label">
          {{ t('menu.sorting') }}
        </p>
        <TheSelectDropdown
          :options="sortingOptions2" alignment="right"
          @update:model-value="(opt: any) => filtersStore.addSortingFilter(opt.value)"
        />
        <button v-if="globalConfiguration?.options?.hasModeMap" class="map-button" @click="toggleMap">
          <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Icons selector">
              <g id="Map">
                <path
                  id="Vector"
                  d="M6.9674 2.24268L2.4624 4.49518V15.7577L6.9674 13.5052M6.9674 2.24268L11.4724 4.49518M6.9674 2.24268V13.5052M11.4724 4.49518L15.9774 2.24268V13.5052L11.4724 15.7577M11.4724 4.49518V15.7577M11.4724 15.7577L6.9674 13.5052"
                  stroke="white" stroke-width="1.50167" stroke-linecap="round" stroke-linejoin="round"
                />
              </g>
            </g>
          </svg>
          {{ t('map.cta') }}
        </button>
      </div>
    </div>
    <p v-if="totalResults && !isMobile" class="results">
      <span>{{ totalResults }}</span> {{ t('list.total') }}
    </p>
    <p v-if="!totalResults && !isMobile" class="results">
      <span>0</span> {{ t('list.total') }}
    </p>
    <div v-if="isMobile" class="results_mobile">
      <p v-if="totalResults" class="results">
        <span>{{ totalResults }}</span> {{ t('list.total') }}
      </p>
      <p v-else class="results">
        <span>0</span> {{ t('list.total') }}
      </p>
      <button type="button" class="sorting_button" @click="isSortingOpen = !isSortingOpen">
        {{ t('menu.sorting') }} <span><ArrowUpDown :size="18" color="var(--yb-color-black)" /></span>
      </button>
    </div>
    <TheMobileSlider
      :is-open="isSortingOpen" :title="t('menu.sorting')" anchor="bottom" :footer="false"
      @close="isSortingOpen = false"
    >
      <TheSortingFilter
        :options="sortingOptions2" :selected-option="sortingMode"
        @select="(val: any) => handleSortingFilter(val)"
      />
    </TheMobileSlider>
    <TheGrid />
    <button v-if="isMobile && globalConfiguration?.options?.hasModeMap" class="map-button" @click="toggleMap">
      <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="Icons selector">
          <g id="Map">
            <path
              id="Vector"
              d="M6.9674 2.24268L2.4624 4.49518V15.7577L6.9674 13.5052M6.9674 2.24268L11.4724 4.49518M6.9674 2.24268V13.5052M11.4724 4.49518L15.9774 2.24268V13.5052L11.4724 15.7577M11.4724 4.49518V15.7577M11.4724 15.7577L6.9674 13.5052"
              stroke="white" stroke-width="1.50167" stroke-linecap="round" stroke-linejoin="round"
            />
          </g>
        </g>
      </svg>
      {{ t('map.label') }}
    </button>
    <Transition name="slide">
      <TheMap v-if="isMapDisplay" @close="toggleMap" />
    </Transition>
  </div>
</template>

<style lang="css" scoped>
.results {
  text-align: left;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-light);
  line-height: var(--yb-text-lineheight);

  @media screen and (width >= 1024px) {
    margin-bottom: var(--yb-spacing-sm);
  }

  span {
    font-weight: var(--yb-text-bold);
  }
}

.results_mobile {
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: var(--yb-text-lineheight);

  @media screen and (width < 1024px) {
    margin-bottom: var(--yb-spacing-sm);
  }

  .sorting_button {
    white-space: nowrap;
  }

  button {
    all: unset;
    display: flex;
    align-items: center;
    font-weight: var(--yb-text-bold);
    cursor: pointer;

    span {
      margin-left: var(--yb-spacing-xs);

      svg {
        position: relative;
        top: 4px;
      }
    }
  }
}

.dropdowns_wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /*margin-top: var(--yb-spacing-base);*/
  margin-bottom: var(--yb-spacing-base);
}

.filters,
.sorting {
  display: flex;
  align-items: center;
}

.filters {
  gap: var(--yb-spacing-xs);
}

.sorting_label {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-black);
  margin-right: var(--yb-spacing-xs);

  @media screen and (width < 1024px) {
    display: none;
  }
}

@media screen and (width < 1024px) {
  .filters,
  .sorting {
    display: none;
  }
}

.map-button {
  display: flex;
  gap: var(--yb-spacing-xs);
  color: var(--yb-button-text-color);
  background-color: var(--yb-color-primary);
  padding: 0.8rem var(--yb-spacing-xl);
  border: none;
  border-radius: var(--yb-radius-full);
  cursor: pointer;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  margin-left: var(--yb-spacing-xs);

  &:hover {
    background-color: var(--yb-color-dark-primary);
    border-color: var(--yb-color-dark-primary);
  }

  @media screen and (width < 1200px) {
    position: sticky;
    justify-content: center;
    align-items: center;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
  }
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 1s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
</style>
